import React, { useState, useEffect } from 'react'
import spektrixAuth from '@lib/spektrix/auth'
import PriorityBookingLoginPrompt from './login-prompt'
import PriorityBookingMembershipRequired from './membership-required'
import PriorityBookingErrorModal from './error-modal'
import { showBookingError } from '@lib/booking-error-handler'

const PriorityBookingHandler = ({ 
  event, 
  instance, 
  children, 
  onAuthStateChange 
}) => {
  const [authState, setAuthState] = useState({
    authenticated: false,
    user: null,
    loading: true
  })
  const [showErrorModal, setShowErrorModal] = useState(false)

  // Check if priority booking is active
  const isPriorityBookingActive = event?.is_priority_booking_active || 
    (event?.priority_booking_starts_at && 
     new Date() >= new Date(event.priority_booking_starts_at) && 
     !event?.is_on_sale)

  // Set up the error handler override immediately if priority booking is active
  // This needs to happen synchronously, not in useEffect
  if (isPriorityBookingActive && !window.__priorityBookingHandlerSet) {
    window.__priorityBookingHandlerSet = true
    window.__originalShowBookingError = window.showBookingError
    
    window.showBookingError = (error, options = {}) => {
      showBookingError(error, {
        ...options,
        onPriorityBookingError: () => {
          // Find the active priority booking component and trigger its modal
          if (window.__activePriorityBookingHandler) {
            window.__activePriorityBookingHandler.setShowErrorModal(true)
          }
        }
      })
    }
  }

  // Check if user has active memberships/subscriptions
  const hasActiveSubscription = (user) => {
    if (!user || !user.subscriptions) {
      return false
    }
    
    const now = new Date()
    return user.subscriptions.some(subscription => {
      if (subscription.endDate) {
        const endDate = new Date(subscription.endDate)
        return endDate > now
      }
      
      if (subscription.startDate) {
        const startDate = new Date(subscription.startDate)
        return startDate <= now
      }
      
      return true
    })
  }

  // Check auth status when priority booking is active
  useEffect(() => {
    if (!isPriorityBookingActive) {
      setAuthState({ authenticated: false, user: null, loading: false })
      return
    }
    
    spektrixAuth.checkAuthStatus().then(result => {
      const newAuthState = {
        authenticated: result.authenticated,
        user: result.user,
        loading: false
      }
      setAuthState(newAuthState)
      
      if (onAuthStateChange) {
        onAuthStateChange(newAuthState)
      }
    })

    const cleanup = spektrixAuth.onAuthChange((authData) => {
      const newAuthState = {
        authenticated: authData.authenticated,
        user: authData.user,
        loading: false
      }
      setAuthState(newAuthState)
      
      if (onAuthStateChange) {
        onAuthStateChange(newAuthState)
      }
    })

    return cleanup
  }, [isPriorityBookingActive, onAuthStateChange])

  const handleLoginSuccess = (user) => {
    const newAuthState = {
      authenticated: true,
      user: user,
      loading: false
    }
    setAuthState(newAuthState)
    
    if (onAuthStateChange) {
      onAuthStateChange(newAuthState)
    }
  }

  // If priority booking is not active, render children normally
  if (!isPriorityBookingActive) {
    return children
  }

  // If still loading auth state, show loading
  if (authState.loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
        <span className="text-gray-600 dark:text-gray-400">Checking access...</span>
      </div>
    )
  }

  // If not authenticated, show login prompt
  if (!authState.authenticated) {
    return (
      <PriorityBookingLoginPrompt 
        eventName={event?.name}
        onLoginSuccess={handleLoginSuccess}
      />
    )
  }

  // If authenticated but no active subscription, show membership required
  if (!hasActiveSubscription(authState.user)) {
    return (
      <PriorityBookingMembershipRequired 
        eventName={event?.name}
      />
    )
  }

  // Register this component instance to receive error modal triggers
  useEffect(() => {
    if (isPriorityBookingActive) {
      window.__activePriorityBookingHandler = { setShowErrorModal }
    }

    return () => {
      if (window.__activePriorityBookingHandler?.setShowErrorModal === setShowErrorModal) {
        window.__activePriorityBookingHandler = null
      }
      // Restore original function on unmount if this was the component that set it up
      if (window.__priorityBookingHandlerSet && window.__originalShowBookingError) {
        window.showBookingError = window.__originalShowBookingError
        window.__priorityBookingHandlerSet = false
        window.__originalShowBookingError = null
      }
    }
  }, [isPriorityBookingActive, setShowErrorModal])

  // Always render error modal if priority booking is active
  const renderWithErrorModal = (content) => (
    <>
      {content}
      <PriorityBookingErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        eventName={event?.name}
      />
    </>
  )

  // If not authenticated, show login prompt with error modal
  if (!authState.authenticated) {
    return renderWithErrorModal(
      <PriorityBookingLoginPrompt 
        eventName={event?.name}
        onLoginSuccess={handleLoginSuccess}
      />
    )
  }

  // If authenticated but no active subscription, show membership required with error modal
  if (!hasActiveSubscription(authState.user)) {
    return renderWithErrorModal(
      <PriorityBookingMembershipRequired 
        eventName={event?.name}
      />
    )
  }

  // User is authenticated and has active subscription, render children with error modal
  return renderWithErrorModal(children)
}

export default PriorityBookingHandler